/**
 * AK-938 — раздел «Наука» (low-fi 21.10, утверждён founder: «да, утверждаю
 * науку и все сезоны», PR #1177). Вкладки «Опыты» (по умолчанию) и «Схемы».
 *
 * Сезон в шапке здесь по умолчанию «Все сезоны» (`seasonLens`): опыт ищут через
 * годы. Колонка «Сезон» первая и остаётся при выборе года — список сужается.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FlaskConical, MapPinPlus, Plus, Search, Users } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PickList } from '@/components/science/PickList';
import { TrialStatusBadge } from '@/components/science/TrialStatusBadge';
import { useCropPicker } from '@/components/fields/card/api';
import { formatRuDateNumeric } from '@/lib/dateFmt';
import { toast } from '@/lib/toast';
import { useConsultantProfile } from '@/lib/consultant/useConsultantProfile';
import { useActiveSeason } from '@/lib/season/SeasonContext';
import { useTrials, useTrialSchemes } from '@/lib/science/api';
import { FACTOR_LABELS, STATUS_LABELS } from '@/lib/science/labels';
import { trialTarget } from '@/lib/science/nav';
import {
  lensSeasonYear,
  useScienceSeasonLens,
  useScienceSeasonLensHost,
} from '@/lib/science/seasonLens';
import type { SchemeStatus, TrialListItem, TrialStatus } from '@/lib/science/types';

export interface ScienceSearch {
  tab?: 'trials' | 'schemes';
}

export function scienceSearch(search: Record<string, unknown>): ScienceSearch {
  return search.tab === 'schemes' ? { tab: 'schemes' } : {};
}

const SCHEME_STATUS: Record<SchemeStatus, string> = {
  draft: 'Черновик',
  active: 'Действует',
  archived: 'В архиве',
};

const ALL = '__all__';

/** Карта id культуры → название (номенклатура кабинета; чужие — прочерк). */
export function useCropNames(): Map<string, string> {
  const picker = useCropPicker();
  return useMemo(() => {
    const m = new Map<string, string>();
    for (const c of picker.data ?? []) if (c.crop_id) m.set(c.crop_id, c.canonical_name ?? c.name);
    return m;
  }, [picker.data]);
}

function TrialsTab() {
  const navigate = useNavigate();
  const { season } = useActiveSeason();
  const { lens } = useScienceSeasonLens();
  const { profile } = useConsultantProfile();
  const cropNames = useCropNames();
  const [q, setQ] = useState('');
  const [farm, setFarm] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [mine, setMine] = useState(false);

  const seasonYear = lensSeasonYear(lens, season?.year);
  const trials = useTrials({ seasonYear, q, mine });
  const items = trials.data?.items ?? [];

  const farmItems = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of items) if (t.farm_id && t.farm_label) m.set(t.farm_id, t.farm_label);
    return [...m].map(([value, label]) => ({ value, label }));
  }, [items]);

  const rows = items.filter(
    (t) => (farm === ALL || t.farm_id === farm) && (status === ALL || t.status === status),
  );
  const clientRows = items.filter((t) => !t.own_cabinet).length;
  const laid = rows.filter((t) => t.status === 'laid' || t.status === 'planned').length;
  const closed = rows.filter(
    (t) => t.status === 'closed_yield' || t.status === 'closed_incomplete',
  ).length;

  function open(t: TrialListItem) {
    const target = trialTarget(t, profile?.client_window?.tenant_id);
    if (!target.ok) {
      toast.info(target.reason);
      return;
    }
    void navigate({
      to: '/science/trials/$fieldId/$trialId',
      params: { fieldId: target.fieldId, trialId: target.trialId },
    });
  }

  return (
    <Stack gap={4}>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_12rem_12rem_auto] md:items-center">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Найти: схема, хозяйство, поле…"
            className="pl-8"
            aria-label="Поиск опытов"
          />
        </div>
        <PickList
          aria-label="Хозяйство"
          value={farm === ALL ? null : farm}
          items={farmItems}
          onChange={(v) => setFarm(v)}
          placeholder="Хозяйство: все"
          searchPlaceholder="Название хозяйства"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger aria-label="Статус">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Статус: все</SelectItem>
            {(Object.keys(STATUS_LABELS) as TrialStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Checkbox id="science-mine" checked={mine} onCheckedChange={(v) => setMine(v === true)} />
          <Label htmlFor="science-mine" className="whitespace-nowrap text-sm">
            Заложены мной
          </Label>
        </div>
      </div>
      {farm !== ALL && (
        <div>
          <Button variant="ghost" size="sm" onClick={() => setFarm(ALL)}>
            Сбросить хозяйство
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        {clientRows > 0 && (
          <Badge variant="outline" className="gap-1">
            <Users className="h-3 w-3" />
            данные клиента
          </Badge>
        )}
        <span>
          {seasonYear ? `Сезон ${seasonYear}` : 'Все сезоны'} · опытов {rows.length}
          {rows.length ? ` · заложено ${laid}, закрыто ${closed}` : ''}
        </span>
      </div>

      {trials.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : trials.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Не удалось загрузить опыты: {trials.error.message}</AlertDescription>
        </Alert>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            {items.length
              ? 'Под фильтр ничего не попало — сбросьте хозяйство или статус.'
              : 'Опытов пока нет. Заложите опыт по схеме: выберите схему, поле и нарисуйте контуры вариантов.'}
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Сезон</TableHead>
                <TableHead>Поле</TableHead>
                <TableHead>Хозяйство</TableHead>
                <TableHead>Схема</TableHead>
                <TableHead>Культура</TableHead>
                <TableHead>Заложен</TableHead>
                <TableHead>Статус</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => {
                const target = trialTarget(t, profile?.client_window?.tenant_id);
                const key = `${t.field_tenant_id ?? ''}:${t.field_trial_id ?? t.label}`;
                return (
                  <TableRow
                    key={key}
                    className={target.ok ? 'cursor-pointer' : 'text-muted-foreground'}
                    onClick={() => open(t)}
                  >
                    <TableCell>{t.season_year}</TableCell>
                    <TableCell className="font-medium">
                      {t.access === 'closed' ? t.label : t.field_label ?? '—'}
                      {!t.own_cabinet && t.access === 'open' && (
                        <Users className="ml-2 inline h-3 w-3 text-muted-foreground" aria-label="данные клиента" />
                      )}
                      {!target.ok && t.access === 'open' && (
                        <div className="text-xs font-normal">{target.reason}</div>
                      )}
                    </TableCell>
                    <TableCell>{t.farm_label ?? '—'}</TableCell>
                    <TableCell>{t.scheme_name ?? t.name ?? '—'}</TableCell>
                    <TableCell>{(t.crop_id && cropNames.get(t.crop_id)) || '—'}</TableCell>
                    <TableCell>{t.laid_at ? formatRuDateNumeric(t.laid_at) : '—'}</TableCell>
                    <TableCell>
                      {t.access === 'closed' ? (
                        <span className="text-xs">доступ закрыт хозяйством</span>
                      ) : (
                        <TrialStatusBadge status={t.status} label={t.status_label} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </Stack>
  );
}

function SchemesTab() {
  const navigate = useNavigate();
  const schemes = useTrialSchemes();
  const items = schemes.data ?? [];

  if (schemes.isLoading) return <Skeleton className="h-40 w-full" />;
  if (schemes.isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Не удалось загрузить схемы: {schemes.error.message}</AlertDescription>
      </Alert>
    );
  }
  if (!items.length) {
    return (
      <Card>
        <CardContent className="flex flex-col items-start gap-3 p-4">
          <p className="text-sm text-muted-foreground">
            Схем пока нет. Схема опыта придумывается один раз — факторы, варианты (контроль, эталон,
            опытные) и препараты с дозами, — а потом закладывается на поля сколько угодно раз.
          </p>
          <Button onClick={() => void navigate({ to: '/science/schemes/new' })}>
            <Plus className="h-4 w-4" />
            Создать схему
          </Button>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Сезон</TableHead>
            <TableHead>Схема</TableHead>
            <TableHead>Факторы</TableHead>
            <TableHead className="text-right">Вариантов</TableHead>
            <TableHead className="text-right">Опытов</TableHead>
            <TableHead>Статус</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((s) => (
            <TableRow
              key={s.id}
              className="cursor-pointer"
              onClick={() =>
                void navigate({ to: '/science/schemes/$schemeId', params: { schemeId: s.id } })
              }
            >
              <TableCell>{s.season_year}</TableCell>
              <TableCell className="font-medium">{s.name}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {s.factors.map((f) => (
                    <Badge key={f} variant="secondary">
                      {FACTOR_LABELS[f]}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-right">{s.variants_count}</TableCell>
              <TableCell className="text-right">{s.trials_count}</TableCell>
              <TableCell>
                <Badge variant={s.status === 'active' ? 'primary' : 'outline'}>
                  {SCHEME_STATUS[s.status]}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function SciencePage({ search }: { search: ScienceSearch }) {
  useScienceSeasonLensHost();
  const navigate = useNavigate();
  const tab = search.tab ?? 'trials';

  return (
    <AppShell>
      <Stack gap={6}>
        <PageHeader
          showBack={false}
          title="Наука"
          description="Опыты на полях и схемы опытов компании"
          actions={
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => void navigate({ to: '/science/schemes/new' })}>
                <Plus className="h-4 w-4" />
                Создать схему
              </Button>
              <Button onClick={() => void navigate({ to: '/science/place', search: {} })}>
                <MapPinPlus className="h-4 w-4" />
                Заложить опыт
              </Button>
            </div>
          }
        />
        <Tabs
          value={tab}
          onValueChange={(v) =>
            void navigate({ to: '/science', search: v === 'schemes' ? { tab: 'schemes' } : {} })
          }
        >
          <TabsList>
            <TabsTrigger value="trials">
              <FlaskConical className="mr-2 h-4 w-4" />
              Опыты
            </TabsTrigger>
            <TabsTrigger value="schemes">Схемы</TabsTrigger>
          </TabsList>
          <TabsContent value="trials" className="pt-4">
            <TrialsTab />
          </TabsContent>
          <TabsContent value="schemes" className="pt-4">
            <SchemesTab />
          </TabsContent>
        </Tabs>
      </Stack>
    </AppShell>
  );
}
